/**
 * Extension of the kendo.ui.DatePicker that will take a timestamp or a system
 * neutral date in the YYYY-MM-DD format.
 *
 * See: locale.parseSystemNeutralDate
 */
import dateConvert from "common/helper/dateConvert";
import locale from "common/managers/locale";
import loginProps from "common/managers/loginProps";
import BaseWidget from "common/widgets/base/BaseWidget";
import popupUtils from "app/widgets/Popup/popupUtils";
import classes from "app/css/widgets/DFDatePicker.module.less";

var DEFAULT_MIN = dateConvert.toDate(loginProps.get().minimumDateRange);
var DEFAULT_MAX = dateConvert.toDate(loginProps.get().maximumDateRange);

const DFDatePicker = BaseWidget(kendo.ui.DatePicker).extend({

    init: function(element, options) {

        if (options.min === undefined) {
            options.min = DEFAULT_MIN;
        }
        if (options.max === undefined) {
            options.max = DEFAULT_MAX;
        }

        if (options.datecompareMsg) {
            $(element).attr("data-datecompare-msg", locale.get(options.datecompareMsg));
        }

        if (options.skipPlaceholder !== true) {
            $(element).attr("placeholder", loginProps.get().dateFormat.toUpperCase());
        }

        kendo.ui.DatePicker.fn.init.call(this, element, options);
        this.wrapper.addClass(classes.DFDatePicker);
        this.dateView.popup.element.addClass(classes.DFDatePickerPopup);

        this.setScreenReaderDescription(locale.get("format.date", loginProps.get().dateFormat.toUpperCase()));

        this.bind("open", () => {
            popupUtils.configurePopup(this.wrapper[0], this.dateView.popup, 251);
        });

    },

    options: {
        name: 'DFDatePicker',
        datecompareMsg: null,
        format: "d",
        skipPlaceholder: false,

        //Get the parse formats that should be attempted in order
        //format is either Year/Month/Day, Day/Month/Year, or Month/Day/Year
        //possible parseFormats match the order of the configured format
        parseFormats: locale.getDateFormats()
    },

    min: function(min) {

        // getter... return as long
        if (min === undefined) {
            var ret = kendo.ui.DatePicker.fn.min.call(this);
            return dateConvert.toLong(ret);
        }

        //  MAGIC: passing invalid min will cause the control to revert to DEFAULT_MIN
        var toMin = DEFAULT_MIN;
        if ($.type(min) == "string" && min.indexOf("-") > 0) {

            // parse argument as system neutral date
            var parts = min.split("-");
            if (parts.length === 3 && parts[0].length === 4) {

                // fuzzed dates should fail
                var dte = new Date(parts[0], parts[1] - 1, parts[2]);
                if (!isNaN(dte.getTime())) {
                    toMin = dte;
                }
            }

        } else if (min instanceof Date) {

            // Date instances are passed along...
            toMin = min;

        } else {

            // maybe a timestamp?
            var parsed = kendo.parseFloat(min);
            if (parsed !== null) {
                toMin = dateConvert.toDate(parsed);
            }
        }

        // better way to ensure property and DOM participate in validation?
        this.element.attr("min", min && toMin.getTime());

        // setter...
        return kendo.ui.DatePicker.fn.min.call(this, toMin);
    },

    /**
     * Returns the value of the DFDatePicker. Passing undefined will return the value as
     * a getter. Passing null or "" (empty) will blank the control. Keep this in mind
     * for binding.
     *
     * @param value
     * @returns {Date|void}
     */
    value: function(value) {
        if (value === undefined) {
            var ret = kendo.ui.DatePicker.fn.value.apply(this, arguments);
            if (ret) {
                return dateConvert.toLong(ret);
            }
            return ret;
        }
        if ($.type(value) == "string" && value.indexOf("-") > 0) {
            // Parse a system neutral date - typically provided via URL API.
            var parts = value.split("-");
            if (parts.length === 3 && parts[0].length === 4) {
                value = new Date(parts[0], parts[1] - 1, parts[2]);
            } else {
                value = "";
            }
        } else if (value instanceof Date) {
            // pass through date value
        } else if (value !== "" && value !== null) {
            // Use a timestamp - default behavior for a DatePicker.
            var parsed = kendo.parseFloat(value);
            if (parsed !== null) {
                value = dateConvert.toDate(parsed);
            } else {
                //Invalid input
                value = "";
            }
        } else {
            //Empty string causes the value to be cleared
            value = "";
        }

        kendo.ui.DatePicker.fn.value.call(this, value);
    },

    /**
     * Patch date picker to ensure that a value outside the valid range is never displayed.
     * @override
     * @private
     */
    _change: function(current) {
        var to = current;
        if (current) {
            var parsed = kendo.parseDate(current, this.options.parseFormats);
            if (parsed && this.options.min && parsed < this.options.min) {
                to = kendo.toString(this.options.min, this.options.format);
            } else if (parsed && this.options.max && parsed > this.options.max) {
                to = kendo.toString(this.options.max, this.options.format);
            }
        }
        return kendo.ui.DatePicker.fn._change.call(this, to);
    },

    /**
     * Gets the value to be used by the DFValidator.  Uses the value from the text input, not the widget.
     * If the user enters an invalid date, the widget's value() method returns undefined, which trips the required field
     * validator.  We want to trigger the custom date validation, since the user did enter a value.
     */
    valueForValidation: function() {
        var ret = this.value();
        try {
            ret = this.element.val();
        } catch (err) {
            ret = this.value();
        }
        return ret;
    },

    /**
     * Default implementation of readonly/enable makes them mutually exclusive.
     * This overrides that and makes them work together
     */
    _handleState: function() {
        if (this._readonlyState) {
            kendo.ui.DatePicker.fn.readonly.call(this, true);
        } else if (!this._enableState) {
            kendo.ui.DatePicker.fn.enable.call(this, false);
        } else {
            kendo.ui.DatePicker.fn.readonly.call(this, false);
            kendo.ui.DatePicker.fn.enable.call(this, true);
        }
    },

    //Initial readonly state
    _readonlyState: false,

    /**
     * Set readonly state.
     */
    readonly: function(to) {
        this.wrapper.toggleClass("readonly", !!to);
        this._readonlyState = !!to;
        this._handleState();
    },

    //Initial enabled state
    _enableState: true,

    /**
     * Set enabled state.
     */
    enable: function(to) {
        this._enableState = !!to;
        this._handleState();

    },

    focus: function() {
        this.element.focus();
    }
});
kendo.ui.plugin(DFDatePicker);
export default DFDatePicker;


